import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import PlanningStatusMenu from './PlanningStatusMenu';
import { PLANNING_TASK_TYPE_LABELS } from '../utils/equipePlanningConstants';
import {
    ABSENCE_TYPE_LABELS,
    ABSENCE_TYPE_SHORT,
    absenceCoversDay,
    addDays,
    buildTaskTooltipRows,
    extractId,
    fetchAbsences,
    fetchAllEntities,
    fetchCurrentUserFresh,
    fmtDayIso,
    fetchPlanningTasks,
    holidayNameFor,
    isoWeekNumber,
    isTaskAssigned,
    moveOwnTaskParEmploye,
    normalizeTaskRow,
    setOwnTaskStatutParEmploye,
    startOfWeekMonday,
    tagExternalTask,
    taskCardCompactLabel,
    taskTypeClass,
} from '../utils/equipePlanningApi';
import { useMercureRefresh } from '../utils/mercure';
import '../styles/MonPlanningRecap.css';

function computeTipPosition(rect) {
    const estW = 320;
    const gap = 10;
    const pad = 16;
    const spaceBelow = window.innerHeight - rect.bottom - gap - pad;
    const spaceAbove = rect.top - gap - pad;
    const placement = spaceBelow >= 300 || spaceBelow >= spaceAbove ? 'below' : 'above';
    const half = estW / 2;
    const left = Math.max(half + pad, Math.min(window.innerWidth - half - pad, rect.left + rect.width / 2));
    // Hauteur bornée au viewport pour ne jamais sortir de l'écran
    const maxHeight = Math.max(120, Math.min(440, placement === 'below' ? spaceBelow : spaceAbove));
    if (placement === 'below') {
        return { placement, style: { top: rect.bottom + gap, left, transform: 'translateX(-50%)', maxHeight } };
    }
    return { placement, style: { bottom: window.innerHeight - rect.top + gap, left, transform: 'translateX(-50%)', maxHeight } };
}

/** Classes d'état d'avancement appliquées à la vignette. */
function chipStatutClass(task) {
    const st = task.statutAvancement || 'a_faire';
    return st !== 'a_faire' ? ` mpr-chip--st-${st}` : '';
}

const MprChipBody = ({ task }) => (
    <>
        {task.statutAvancement === 'terminee' && <span className="mpr-chip-check" aria-hidden>✓</span>}
        {/* Tâche d'une autre entité (invitation cross-entité) : pastille couleur */}
        {task._externe && (
            <span
                className="mpr-chip-ent"
                style={{ background: task._entiteCouleur }}
                title={`Tâche ${task._entiteNom}`}
                aria-label={`Tâche de l'entité ${task._entiteNom}`}
            />
        )}
        {taskCardCompactLabel(task)}
    </>
);

const MprTaskChip = ({ task, canManage, onOpenStatusMenu, suppressClickRef }) => {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `mpr-task-${task.id}`,
        disabled: !canManage,
        data: { task },
    });

    const show = useCallback(() => {
        if (ref.current) setTip(computeTipPosition(ref.current.getBoundingClientRect()));
    }, []);
    const hide = useCallback(() => setTip(null), []);

    useEffect(() => {
        if (isDragging) setTip(null);
    }, [isDragging]);

    const typeLabel = PLANNING_TASK_TYPE_LABELS[task.typeTache] || task.typeTache;
    const headline = task.numeroErp || task.nomStandClient || task.salonNom || 'Tâche';
    const rows = buildTaskTooltipRows(task).filter((r) => r.label !== 'Type');
    const prioClass = task.priorite === 'urgent' || task.priorite === 'elevee'
        ? ` mpr-chip--prio-${task.priorite}`
        : '';

    const handleClick = (e) => {
        if (!canManage || !onOpenStatusMenu) return;
        // Un drag qui vient de se terminer déclenche aussi un click : on l'ignore
        if (suppressClickRef?.current && Date.now() - suppressClickRef.current < 200) return;
        setTip(null);
        onOpenStatusMenu(task, e.currentTarget.getBoundingClientRect());
    };

    const setRefs = (node) => {
        setNodeRef(node);
        ref.current = node;
    };

    return (
        <>
            <span
                ref={setRefs}
                className={`mpr-chip mpr-chip--${taskTypeClass(task.typeTache)}${prioClass}${chipStatutClass(task)}${canManage ? ' mpr-chip--manageable' : ''}${isDragging ? ' mpr-chip--dragging' : ''}`}
                onMouseEnter={show}
                onMouseLeave={hide}
                onClick={handleClick}
                role={canManage ? 'button' : undefined}
                tabIndex={canManage ? 0 : undefined}
                {...(canManage ? attributes : {})}
                {...(canManage ? listeners : {})}
            >
                <MprChipBody task={task} />
            </span>
            {tip && !isDragging && createPortal(
                <div
                    className={`mpr-popover mpr-popover--${tip.placement}`}
                    style={tip.style}
                    role="tooltip"
                >
                    <div className="mpr-popover-hd">
                        <span className={`mpr-popover-type mpr-chip--${taskTypeClass(task.typeTache)}`}>{typeLabel}</span>
                        <p className="mpr-popover-title">{headline}</p>
                    </div>
                    {rows.length > 0 && (
                        <dl className="mpr-popover-dl">
                            {rows.map(({ label, value, accent }) => (
                                <div key={label} className="mpr-popover-row">
                                    <dt>{label}</dt>
                                    <dd>
                                        {accent ? (
                                            <span className={`mpr-prio-chip mpr-prio-chip--${accent}`}>{value}</span>
                                        ) : value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}
                    {canManage && (
                        <p className="mpr-popover-hint">Cliquez pour changer le statut · glissez pour déplacer</p>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
};

/** Colonne jour droppable (déplacement des tâches de l'utilisateur).
    variant="month" : en-tête compact (numéro du jour seul, le jour de la
    semaine est porté par la ligne d'en-tête du calendrier). */
const MprDayColumn = ({ col, isToday, holiday, absence, canManage, isDragActive, variant = 'default', showMonth = false, children }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `mprday|${col.iso}`,
        disabled: !canManage,
    });
    const isOff = Boolean(holiday || absence);

    return (
        <div
            ref={setNodeRef}
            className={`mpr-daycol${variant === 'month' ? ' mpr-daycol--month' : ''}${isToday ? ' mpr-daycol--today' : ''}${isOff ? ' mpr-daycol--off' : ''}${canManage && isDragActive && isOver ? ' mpr-daycol--over' : ''}`}
        >
            {variant === 'month' ? (
                <div className="mpr-daycol-head mpr-daycol-head--month">
                    <span className="mpr-daycol-num">{col.dayNum}</span>
                    {showMonth && <span className="mpr-daycol-mo">{col.monthShort}</span>}
                </div>
            ) : (
                <div className="mpr-daycol-head">
                    <span className="mpr-daycol-wd">{col.weekday}</span>
                    <span className="mpr-daycol-num">{col.dayNum}</span>
                    <span className="mpr-daycol-mo">{col.monthShort}</span>
                </div>
            )}
            {holiday && (
                <div className="mpr-day-flag mpr-day-flag--ferie" title={holiday}>
                    Férié · {holiday}
                </div>
            )}
            {absence && (
                <div
                    className={`mpr-day-flag mpr-day-flag--abs mpr-day-flag--${absence.typeAbsence}`}
                    title={ABSENCE_TYPE_LABELS[absence.typeAbsence] || 'Absence'}
                >
                    {ABSENCE_TYPE_SHORT[absence.typeAbsence] || 'Absent'}
                </div>
            )}
            <div className="mpr-daycol-tasks">
                {children}
            </div>
        </div>
    );
};

/**
 * Récap personnel du planning (uniquement les tâches assignées à l'utilisateur courant).
 * Vue 1 ou 2 semaines, navigable, avec semaine ISO, jours fériés et absences.
 * `canManage` : l'utilisateur peut déplacer ses tâches dans la période affichée
 * et changer leur statut — chaque changement est tracé pour le manager.
 */
const MonPlanningRecapCard = ({ userId, canManage = true }) => {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('1w');
    const [anchorDate, setAnchorDate] = useState(() => new Date());
    const [tasks, setTasks] = useState([]);
    const [absences, setAbsences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [statusMenu, setStatusMenu] = useState(null); // { task, rect }
    const [activeTask, setActiveTask] = useState(null);
    const suppressClickRef = useRef(0);

    // Référentiel entités + entité de l'utilisateur : sert à badger les tâches
    // venant d'un autre board (collaborateur invité sur une autre entité).
    const [entitesById, setEntitesById] = useState({});
    const [myEntityId, setMyEntityId] = useState(null);

    useEffect(() => {
        let active = true;
        Promise.all([fetchAllEntities(), fetchCurrentUserFresh()])
            .then(([ents, me]) => {
                if (!active) return;
                const map = {};
                ents.forEach((e) => { map[String(e.id)] = e; });
                setEntitesById(map);
                setMyEntityId(extractId(me?.entityReference));
            })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    );

    const todayIso = fmtDayIso(new Date());

    const dayColumns = useMemo(() => {
        const start = startOfWeekMonday(anchorDate);
        const count = viewMode === '1m' ? 28 : viewMode === '2w' ? 14 : 7;
        const cols = [];
        for (let i = 0; i < count; i += 1) {
            const d = addDays(start, i);
            cols.push({
                iso: fmtDayIso(d),
                date: d,
                weekday: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
                dayNum: d.getDate(),
                monthShort: d.toLocaleDateString('fr-FR', { month: 'short' }),
                isWeekend: d.getDay() === 0 || d.getDay() === 6,
            });
        }
        return cols;
    }, [viewMode, anchorDate]);

    const rangeStart = dayColumns[0]?.iso;
    const rangeEnd = dayColumns[dayColumns.length - 1]?.iso;

    const weekLabel = useMemo(() => {
        if (!dayColumns.length) return '';
        const wStart = isoWeekNumber(dayColumns[0].date);
        if (viewMode !== '1w') {
            const wEnd = isoWeekNumber(dayColumns[dayColumns.length - 1].date);
            return wStart === wEnd ? `Semaine ${wStart}` : `Semaines ${wStart}–${wEnd}`;
        }
        return `Semaine ${wStart}`;
    }, [dayColumns, viewMode]);

    const rangeLabel = useMemo(() => {
        if (!dayColumns.length) return '';
        const fmt = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        return `${fmt(dayColumns[0].date)} – ${fmt(dayColumns[dayColumns.length - 1].date)}`;
    }, [dayColumns]);

    const isCurrentPeriod = useMemo(
        () => dayColumns.some((c) => c.iso === todayIso),
        [dayColumns, todayIso],
    );

    const shiftPeriod = useCallback((dir) => {
        const step = (viewMode === '1m' ? 28 : 7) * dir;
        setAnchorDate((prev) => addDays(startOfWeekMonday(prev), step));
    }, [viewMode]);

    const reload = useCallback(async () => {
        if (!userId || !rangeStart || !rangeEnd) return;
        const [rows, abs] = await Promise.all([
            fetchPlanningTasks({ assigneA: userId, rangeStart, rangeEnd }),
            fetchAbsences({ rangeStart, rangeEnd }).catch(() => []),
        ]);
        setTasks(rows
            .map(normalizeTaskRow)
            .filter(isTaskAssigned)
            // Badge sur les tâches d'une autre entité que la mienne (invitation)
            .map((t) => (myEntityId != null && t.entiteId != null && Number(t.entiteId) !== Number(myEntityId)
                ? tagExternalTask(t, entitesById)
                : t)));
        setAbsences(abs.filter((a) => Number(a.utilisateurId) === Number(userId)));
    }, [userId, rangeStart, rangeEnd, myEntityId, entitesById]);

    useEffect(() => {
        if (!userId || !rangeStart || !rangeEnd) return undefined;
        let active = true;
        setLoading(true);
        reload()
            .catch(() => { if (active) { setTasks([]); setAbsences([]); } })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [userId, rangeStart, rangeEnd, reload]);

    // Temps réel : une tâche assignée / déplacée par le manager apparaît sans actualiser
    useMercureRefresh(['equipe_planning_tasks', 'equipe_absences'], () => { reload().catch(() => {}); });

    const tasksByDay = useMemo(() => {
        const map = {};
        dayColumns.forEach((c) => { map[c.iso] = []; });
        tasks.forEach((t) => {
            if (map[t.jourAssigne]) map[t.jourAssigne].push(t);
        });
        Object.keys(map).forEach((iso) => {
            map[iso].sort((a, b) => a.positionJour - b.positionJour || a.id - b.id);
        });
        return map;
    }, [tasks, dayColumns]);

    const absenceByDay = useMemo(() => {
        const map = {};
        absences.forEach((a) => {
            dayColumns.forEach((c) => {
                if (absenceCoversDay(a, c.iso) && !map[c.iso]) {
                    map[c.iso] = a;
                }
            });
        });
        return map;
    }, [absences, dayColumns]);

    // On masque les week-ends sans tâche pour garder un récap clair
    const visibleDays = useMemo(
        () => dayColumns.filter((c) => !c.isWeekend || (tasksByDay[c.iso] || []).length > 0),
        [dayColumns, tasksByDay],
    );

    // Vue mois : semaines empilées. Les week-ends ne sont montrés (sur toutes
    // les semaines, pour garder les colonnes alignées) que si l'un d'eux a une tâche.
    const monthWeeks = useMemo(() => {
        if (viewMode !== '1m') return null;
        const showWeekend = dayColumns.some(
            (c) => c.isWeekend && (tasksByDay[c.iso] || []).length > 0,
        );
        const weeks = [];
        for (let i = 0; i < dayColumns.length; i += 7) {
            const days = dayColumns
                .slice(i, i + 7)
                .filter((c) => showWeekend || !c.isWeekend);
            weeks.push({ days, weekNum: isoWeekNumber(dayColumns[i].date) });
        }
        return { weeks, cols: showWeekend ? 7 : 5 };
    }, [viewMode, dayColumns, tasksByDay]);

    const totalTasks = tasks.length;

    // Récap par type de tâche sur la période affichée
    const typeCounts = useMemo(() => {
        const counts = new Map();
        tasks.forEach((t) => {
            const type = taskTypeClass(t.typeTache);
            counts.set(type, (counts.get(type) || 0) + 1);
        });
        return [...counts.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([type, n]) => ({ type, n, label: PLANNING_TASK_TYPE_LABELS[type] || type }));
    }, [tasks]);

    // ── Déplacement (drag & drop) de ses propres tâches ─────────────────────
    const handleDragStart = (event) => {
        const t = event.active?.data?.current?.task;
        if (t) setActiveTask(t);
    };

    const handleDragCancel = () => setActiveTask(null);

    const handleDragEnd = async (event) => {
        setActiveTask(null);
        suppressClickRef.current = Date.now();
        const { active, over } = event;
        if (!over || !canManage) return;
        const task = active.data?.current?.task;
        const overId = String(over.id);
        if (!task || !overId.startsWith('mprday|')) return;
        const targetIso = overId.slice('mprday|'.length);
        if (!targetIso || targetIso === task.jourAssigne) return;

        const position = (tasksByDay[targetIso] || []).length;
        // Optimiste : la vignette change de jour immédiatement
        setTasks((prev) => prev.map((t) => (
            t.id === task.id
                ? { ...t, jourAssigne: targetIso, positionJour: position, majEmployeType: 'deplacement', majEmployeAt: new Date().toISOString() }
                : t
        )));
        setSaveError('');
        try {
            await moveOwnTaskParEmploye(task, targetIso, position);
        } catch (e) {
            console.error(e);
            setSaveError('Déplacement impossible — planning rechargé.');
            reload().catch(() => {});
        }
    };

    // ── Changement de statut ─────────────────────────────────────────────────
    const openStatusMenu = useCallback((task, rect) => {
        setStatusMenu({ task, rect });
    }, []);

    const handlePickStatus = async (statut) => {
        const task = statusMenu?.task;
        setStatusMenu(null);
        if (!task || (task.statutAvancement || 'a_faire') === statut) return;
        setTasks((prev) => prev.map((t) => (
            t.id === task.id
                ? { ...t, statutAvancement: statut, majEmployeType: 'statut', majEmployeAt: new Date().toISOString() }
                : t
        )));
        setSaveError('');
        try {
            await setOwnTaskStatutParEmploye(task, statut);
        } catch (e) {
            console.error(e);
            setSaveError('Changement de statut impossible — planning rechargé.');
            reload().catch(() => {});
        }
    };

    const renderDayCol = (col, extra = {}) => {
        const dayTasks = tasksByDay[col.iso] || [];
        return (
            <MprDayColumn
                key={col.iso}
                col={col}
                isToday={col.iso === todayIso}
                holiday={holidayNameFor(col.iso)}
                absence={absenceByDay[col.iso]}
                canManage={canManage}
                isDragActive={Boolean(activeTask)}
                {...extra}
            >
                {dayTasks.length === 0 ? (
                    <span className="mpr-daycol-empty">—</span>
                ) : (
                    dayTasks.map((t) => (
                        <MprTaskChip
                            key={t.id}
                            task={t}
                            canManage={canManage}
                            onOpenStatusMenu={openStatusMenu}
                            suppressClickRef={suppressClickRef}
                        />
                    ))
                )}
            </MprDayColumn>
        );
    };

    const board = monthWeeks ? (
        <div
            className="mpr-month-grid"
            style={{ gridTemplateColumns: `34px repeat(${monthWeeks.cols}, minmax(0, 1fr))` }}
        >
            {/* Ligne d'en-tête : jours de la semaine, une seule fois */}
            <span className="mpr-month-corner" aria-hidden="true" />
            {monthWeeks.weeks[0].days.map((c) => (
                <span key={c.iso} className="mpr-month-hd">{c.weekday}</span>
            ))}
            {monthWeeks.weeks.map(({ days, weekNum }, wi) => (
                <React.Fragment key={weekNum}>
                    <span className="mpr-month-weeklabel">S{weekNum}</span>
                    {days.map((col, di) => renderDayCol(col, {
                        variant: 'month',
                        // Mois affiché sur la 1re case et à chaque changement de mois
                        showMonth: (wi === 0 && di === 0) || col.dayNum === 1,
                    }))}
                </React.Fragment>
            ))}
        </div>
    ) : (
        <div className={`mpr-week mpr-week--${viewMode}`}>
            {visibleDays.map(renderDayCol)}
        </div>
    );

    return (
        <div className="ed-card mpr-card">
            <div className="ed-card-hd mpr-hd">
                <div className="mpr-hd-left">
                    <span className="ed-card-title">
                        <CalendarDays size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                        Mon planning
                    </span>
                    <span className="mpr-hd-week">{weekLabel}</span>
                    <span className="mpr-hd-sub">{rangeLabel}</span>
                    {totalTasks > 0 && (
                        <span className="mpr-hd-count">{totalTasks} tâche{totalTasks > 1 ? 's' : ''}</span>
                    )}
                </div>
                <div className="mpr-controls">
                    <div className="mpr-nav" role="group" aria-label="Navigation des semaines">
                        <button
                            type="button"
                            className="mpr-nav-btn"
                            onClick={() => shiftPeriod(-1)}
                            aria-label="Période précédente"
                        >
                            <ChevronLeft size={15} strokeWidth={2.5} />
                        </button>
                        <button
                            type="button"
                            className="mpr-nav-today"
                            onClick={() => setAnchorDate(new Date())}
                            disabled={isCurrentPeriod}
                        >
                            Aujourd&apos;hui
                        </button>
                        <button
                            type="button"
                            className="mpr-nav-btn"
                            onClick={() => shiftPeriod(1)}
                            aria-label="Période suivante"
                        >
                            <ChevronRight size={15} strokeWidth={2.5} />
                        </button>
                    </div>
                    <div className="mpr-toggle" role="group" aria-label="Période">
                        <button
                            type="button"
                            className={`mpr-toggle-btn${viewMode === '1w' ? ' active' : ''}`}
                            onClick={() => setViewMode('1w')}
                        >
                            1 semaine
                        </button>
                        <button
                            type="button"
                            className={`mpr-toggle-btn${viewMode === '2w' ? ' active' : ''}`}
                            onClick={() => setViewMode('2w')}
                        >
                            2 semaines
                        </button>
                        <button
                            type="button"
                            className={`mpr-toggle-btn${viewMode === '1m' ? ' active' : ''}`}
                            onClick={() => setViewMode('1m')}
                        >
                            1 mois
                        </button>
                        <button
                            type="button"
                            className="mpr-toggle-link"
                            onClick={() => navigate('/admin/equipe-planning')}
                        >
                            Planning complet
                        </button>
                    </div>
                </div>
            </div>

            {typeCounts.length > 0 && (
                <div className="mpr-summary">
                    <span className="mpr-summary-label">Sur la période :</span>
                    {typeCounts.map(({ type, n, label }) => (
                        <span key={type} className={`mpr-sum-chip mpr-chip--${type}`}>
                            {label} <b>×{n}</b>
                        </span>
                    ))}
                    {canManage && (
                        <span className="mpr-manage-hint">
                            Glissez une tâche pour la déplacer · cliquez pour changer son statut
                        </span>
                    )}
                </div>
            )}

            {saveError && <p className="mpr-save-error">{saveError}</p>}

            {loading && totalTasks === 0 ? (
                <p className="mpr-empty">Chargement…</p>
            ) : canManage ? (
                <DndContext
                    sensors={sensors}
                    autoScroll={false}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    {board}
                    <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]} style={{ zIndex: 10080 }}>
                        {activeTask ? (
                            <span className={`mpr-chip mpr-chip--${taskTypeClass(activeTask.typeTache)} mpr-chip--overlay`}>
                                <MprChipBody task={activeTask} />
                            </span>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            ) : (
                board
            )}

            {!loading && totalTasks === 0 && (
                <p className="mpr-empty">Aucune tâche assignée sur cette période.</p>
            )}

            {statusMenu && (
                <PlanningStatusMenu
                    task={statusMenu.task}
                    anchorRect={statusMenu.rect}
                    onPick={handlePickStatus}
                    onClose={() => setStatusMenu(null)}
                />
            )}
        </div>
    );
};

export default MonPlanningRecapCard;
